"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { JobProfileModal } from "../components/JobProfileModal";
import { JobProfileTable } from "../components/JobProfileTable";
import { JobProfile } from "../types";

// Position value mapping for migration
const positionValueMapping: Record<string, string> = {
  ausbildung: "training",
  hilfskraft: "assistant",
  fachkraft: "specialist"
};

export default function SettingsProfiles() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<JobProfile[]>([]);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<
    JobProfile | undefined
  >();
  const [companyId, setCompanyId] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from("job_profiles")
        .select("*")
        .eq("company_id", companyId);

      if (profilesError) throw profilesError;

      // Migrate position values if needed
      const migratedProfiles =
        profilesData?.map((profile) => {
          if (profile.position && positionValueMapping[profile.position]) {
            return {
              ...profile,
              position: positionValueMapping[profile.position]
            };
          }
          return profile;
        }) || [];

      setProfiles(migratedProfiles);

      // Update profiles in database if migration was needed
      if (
        profilesData &&
        migratedProfiles.some(
          (profile, index) => profile.position !== profilesData[index].position
        )
      ) {
        await migrateProfiles(profilesData, migratedProfiles);
      }
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Profile",
        variant: "destructive"
      });
    }
  }, [companyId, toast]);

  const fetchCompanyId = useCallback(async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", profile.id)
        .single();
      if (profileError) throw profileError;
      setCompanyId(profileData?.company_id);
    } catch (error) {
      console.error("Error fetching company ID:", error);
      setCompanyId(null);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Unternehmensdaten",
        variant: "destructive"
      });
    }
  }, [profile.id, toast]);

  useEffect(() => {
    if (!profile.anonymous) {
      fetchCompanyId();
    }
  }, [profile, fetchCompanyId]);

  useEffect(() => {
    if (!profile.anonymous && companyId) {
      fetchProfiles();
    }
  }, [profile, companyId, fetchProfiles]);

  const migrateProfiles = async (
    oldProfiles: JobProfile[],
    newProfiles: JobProfile[]
  ) => {
    try {
      for (let i = 0; i < oldProfiles.length; i++) {
        if (oldProfiles[i].position !== newProfiles[i].position) {
          const { error } = await supabase
            .from("job_profiles")
            .update({
              position: newProfiles[i].position,
              updated_at: new Date().toISOString()
            })
            .eq("id", oldProfiles[i].id);

          if (error) throw error;
        }
      }
    } catch (error) {
      console.error("Error migrating profiles:", error);
    }
  };

  const handleProfileSave = async (
    profileData: Omit<
      JobProfile,
      "id" | "company_id" | "created_at" | "updated_at"
    >
  ) => {
    try {
      if (selectedProfile) {
        // Update existing profile
        const { error } = await supabase
          .from("job_profiles")
          .update({
            ...profileData,
            updated_at: new Date().toISOString()
          })
          .eq("id", selectedProfile.id);

        if (error) throw error;
        toast({
          title: "Erfolg",
          description: "Profil erfolgreich aktualisiert"
        });
      } else {
        // Create new profile
        const { error } = await supabase.from("job_profiles").insert({
          ...profileData,
          company_id: companyId
        });

        if (error) throw error;
        toast({
          title: "Erfolg",
          description: "Profil erfolgreich erstellt"
        });
      }

      fetchProfiles();
      setIsProfileModalOpen(false);
      setSelectedProfile(undefined);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Speichern des Profils",
        variant: "destructive"
      });
    }
  };

  const handleProfileDelete = async (profile: JobProfile) => {
    try {
      const { error } = await supabase
        .from("job_profiles")
        .delete()
        .eq("id", profile.id);

      if (error) throw error;
      toast({
        title: "Erfolg",
        description: "Profil erfolgreich gelöscht"
      });
      fetchProfiles();
    } catch (error) {
      console.error("Error deleting profile:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen des Profils",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Stellenprofile</h2>
        <p className="text-sm text-gray-500">
          Verwalte die Stellenprofile deines Unternehmens
        </p>
      </div>
      <div>
        <JobProfileTable
          profiles={profiles}
          onEdit={(profile) => {
            setSelectedProfile(profile);
            setIsProfileModalOpen(true);
          }}
          onDelete={handleProfileDelete}
          onAdd={() => {
            setSelectedProfile(undefined);
            setIsProfileModalOpen(true);
          }}
        />
      </div>

      <JobProfileModal
        open={isProfileModalOpen}
        onOpenChange={setIsProfileModalOpen}
        onSave={handleProfileSave}
        profile={selectedProfile}
        title={selectedProfile ? "Profil bearbeiten" : "Neues Profil"}
      />
    </div>
  );
}
